import type { Alert } from './@types/alert';
import type { DeviceCount, OnlineDevice } from './@types/device';
import type { Group } from './@types/group';
import type { GtfsFromApi } from './@types/gtfs-object';
import type { OnDemandRoute } from './@types/on-demand-route';
import type { RailArrival, RailDeparture, RailStationLink } from './@types/rail';
import type { TripUpdate } from './@types/trip-updates';
import type { Vehicle } from './@types/vehicle';
import { BASE_URL, getCollection, getJson } from './libs/api-helper';

enum GtfsTableName {
  CALENDAR_DATES = 'calendarDates',
  CALENDARS = 'calendars',
  ROUTES = 'routes',
  SHAPES = 'shapes',
  STOPS = 'stops',
  TRIPS = 'trips',
}

export const gtfsApi = {
  /**
   * Call all ressources endpoints from GTFS API
   */
  async getAllGtfsResources(groupId: string, gtfsId: string): Promise<GtfsFromApi | null> {
    const gtfsResources: { [key: string]: any } = {};
    const promises = Object.keys(GtfsTableName).map(async endpoint => {
      const resourceName = endpoint.toLowerCase();
      const result = await getCollection(groupId, `gtfs/${gtfsId}/${resourceName}`);
      gtfsResources[resourceName] = result;
    });
    try {
      await Promise.all(promises);
    } catch {
      return null;
    }
    return gtfsResources;
  },
};

export default {
  getGroups(): Promise<Array<Group>> {
    const qParams = new URLSearchParams([['feature', 'display_info']]);
    return getJson(`${BASE_URL}/api/v4/pub-groups?${qParams}`);
  },

  getGroup(groupId: string): Promise<Group> {
    const qParams = new URLSearchParams([['feature', 'display_info']]);
    return getJson(`${BASE_URL}/api/v4/pub-groups/${groupId}?${qParams}`);
  },

  getOnlineDevices(
    groupId: string,
    filters: { routeIds?: string[]; onDemandRouteIds?: string[]; deviceId?: string; tripId?: string } = {},
  ): Promise<Array<OnlineDevice>> {
    const qParams = new URLSearchParams();
    if (filters.routeIds) {
      filters.routeIds.forEach(routeId => {
        qParams.append('route_id', routeId);
      });
    }
    filters.onDemandRouteIds?.forEach(onDemandRouteId => {
      qParams.append('on_demand_route_id', onDemandRouteId);
    });
    if (filters.deviceId) qParams.append('device_id', filters.deviceId);
    if (filters.tripId) qParams.append('trip_id', filters.tripId);

    return getCollection(groupId, 'devices/online', qParams, 'v4');
  },

  getOnlineDevicesCount(groupId: string, routeIds: Array<string> = []): Promise<Array<DeviceCount>> {
    const qParams = new URLSearchParams();
    if (routeIds.length > 0) {
      routeIds.forEach(route => {
        qParams.append('route_id', route);
      });
    }
    return getCollection(groupId, 'devices/online/count', qParams);
  },

  getAlerts(groupId: string): Promise<Array<Alert>> {
    return getCollection(groupId, 'alerts');
  },

  sendAppEvent(groupId: string) {
    if (!groupId) return;
    fetch(`${BASE_URL}/api/v4/groups/${groupId}/session-counters/info`, { method: 'POST' });
  },

  async getTripUpdates(date: string, groupId: string, gtfsId: string): Promise<Array<TripUpdate>> {
    const qParams = new URLSearchParams([
      ['date', date],
      ['gtfs_id', gtfsId],
    ]);
    return getCollection(groupId, 'trip-updates/search', qParams, 'v4');
  },

  /**
   * Fetches the list of vehicles for a given group.
   */
  getVehicles(groupId: string): Promise<Array<Vehicle>> {
    return getCollection(groupId, 'vehicles/public');
  },

  /** Active and inactive on-demand routes (features `on_demand` and `device_event_sourcing`). */
  getOnDemandRoutes(groupId: string): Promise<Array<OnDemandRoute>> {
    return getCollection(groupId, 'on-demand-routes/public', null, 'v4');
  },

  getOnDemandRoute(groupId: string, routeId: string): Promise<OnDemandRoute> {
    return getJson(
      `${BASE_URL}/api/v4/groups/${groupId}/on-demand-routes/public/${encodeURIComponent(routeId)}`,
    );
  },

  /** Stops of the network attached to a SNCF station (feature `rail_data`). */
  getRailStationLinks(groupId: string): Promise<Array<RailStationLink>> {
    return getCollection(groupId, 'rail/station-links', null, 'v4');
  },

  /**
   * Departure board of the station(s) a stop is attached to. Without a window the
   * API serves the next hour, sorted by scheduled departure.
   */
  getRailDepartures(groupId: string, stopId: string): Promise<Array<RailDeparture>> {
    const qParams = new URLSearchParams([['stop_id', stopId]]);
    return getCollection(groupId, 'rail/departures', qParams, 'v4');
  },

  /**
   * Arrival board of the station(s) a stop is attached to, the mirror of the departures:
   * the next hour by default, sorted by scheduled arrival.
   */
  getRailArrivals(groupId: string, stopId: string): Promise<Array<RailArrival>> {
    const qParams = new URLSearchParams([['stop_id', stopId]]);
    return getCollection(groupId, 'rail/arrivals', qParams, 'v4');
  },
};
